import axios from "axios";
import config from "../config";
import * as url from "../helpers/url_helper";

const { api } = config;

export interface WorkOrderAttachmentItem {
  id: string;
  tenantId: string;
  tenantName?: string;
  workOrderId: string;
  workOrderNumber?: string;
  clientId: string;
  clientName?: string;
  fileName: string;
  filePath: string;
  fileSizeBytes: number;
  isDeleted: boolean;
}

export interface WorkOrderAttachmentResponse {
  items: WorkOrderAttachmentItem[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface WorkOrderAttachmentCreatePayload {
  tenantId: string;
  workOrderId: string;
  clientId: string;
  attachment: File;
}

export interface WorkOrderAttachmentApiResponse {
  id: string;
  isSuccess: boolean;
  notFound: boolean;
  message: string;
  modelErrors: any[];
  result: WorkOrderAttachmentItem;
}

// Get auth token from Redux Persist
const getAuthToken = () => {
  try {
    const persistedState = sessionStorage.getItem('persist:root');
    if (persistedState) {
      const parsed = JSON.parse(persistedState);
      if (parsed.Login) {
        const loginState = JSON.parse(parsed.Login);
        if (loginState.user && Object.keys(loginState.user).length > 0) {
          return loginState.user.token || null;
        }
      }
    }
  } catch (e) {
    console.error("Error reading auth token from Redux Persist:", e);
  }
  return null;
};

// Get all attachments for a work order
export const getWorkOrderAttachments = async (
  workOrderId: string,
  pageNumber: number = 1,
  pageSize: number = 100
): Promise<WorkOrderAttachmentResponse> => {
  try {
    const token = getAuthToken();
    const response = await axios.get(`${api.API_URL}${url.GET_WORK_ORDER_ATTACHMENTS}`, {
      params: {
        workOrderId,
        pageNumber,
        pageSize,
      },
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json;odata.metadata=minimal;odata.streaming=true",
      },
    });
    
    return response as unknown as WorkOrderAttachmentResponse;
  } catch (error) {
    return {
      items: [],
      pageNumber,
      pageSize,
      totalCount: 0,
      totalPages: 0,
      hasPreviousPage: false,
      hasNextPage: false,
    };
  }
};

// Upload a new attachment
export const uploadWorkOrderAttachment = async (
  payload: WorkOrderAttachmentCreatePayload
): Promise<WorkOrderAttachmentItem> => {
  const token = getAuthToken();
  const formData = new FormData();
  formData.append("TenantId", payload.tenantId);
  formData.append("WorkOrderId", payload.workOrderId);
  formData.append("ClientId", payload.clientId);
  formData.append("Attachment", payload.attachment);

  const response = await axios.post<WorkOrderAttachmentApiResponse>(
    `${api.API_URL}${url.ADD_WORK_ORDER_ATTACHMENT}`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json;odata.metadata=minimal;odata.streaming=true",
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data.result;
};

// Delete an attachment
export const deleteWorkOrderAttachment = async (attachmentId: string): Promise<void> => {
  const token = getAuthToken();
  await axios.delete(`${api.API_URL}${url.DELETE_WORK_ORDER_ATTACHMENT}/${attachmentId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json;odata.metadata=minimal;odata.streaming=true",
    },
  });
};

// Download attachment file - uses direct filePath URL
export const downloadWorkOrderAttachment = async (
  attachment: WorkOrderAttachmentItem
): Promise<Blob> => {
  const token = getAuthToken();
  const response = await axios.get(attachment.filePath, {
    responseType: "blob",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};


